import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import ROUTES from '../../../constants/routes';
import { getCart } from '../../../reducers/cart';
import useMaterialById from '../../../services/materials/use-material-by-id';
import session from '../../../utils/session';
import serializeQueryParams from '../../../utils/url/serialize-query-params';
import { changeState } from '../reducers';

export default function usePaymentCart() {
    const queryParams = serializeQueryParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const cartData = useSelector((state: RootState) => state.main.cart);
    const materialId = queryParams.materialId ? Number(queryParams.materialId) : null;
    const { data: material, isSuccess: isMaterialLoaded, isError: isMaterialError } = useMaterialById(materialId);

    // Step back if a same-origin page is behind us (Navigation API, else history size); else cart.
    const exitCheckout = () => {
        const { navigation } = window as unknown as { navigation?: { canGoBack: boolean } };
        const canGoBack = navigation ? navigation.canGoBack : window.history.length > 1;

        canGoBack ? navigate(-1) : navigate(ROUTES.WARENKORB.path, { replace: true });
    };

    useEffect(() => {
        if (!cartData.loaded) {
            dispatch(getCart());

            return;
        }

        if (materialId) {
            if (isMaterialError) {
                exitCheckout();
                dispatch(changeState({ isDataProcessing: false }));

                return;
            }

            if (!isMaterialLoaded || !material) {
                return;
            }

            const isFree = material.price === '0,00';
            const isOwned = session.isLoggedIn() && Boolean(material.aclId || material.hasAclFree);
            const canPurchaseMaterial = !isFree && !isOwned;

            if (!canPurchaseMaterial) {
                exitCheckout();
                dispatch(changeState({ isDataProcessing: false }));

                return;
            }

            const singleCartData = {
                cartItems: [
                    {
                        finalPrice: material.price,
                        material,
                        originalPrice: material.price,
                        total: material.price
                    }
                ],
                finalPrice: material.price,
                loaded: true
            } as unknown as Cart;

            dispatch(changeState({ paymentCart: singleCartData }));
            dispatch(changeState({ isDataProcessing: false }));

            return;
        }

        if (!cartData.cartItems?.length) {
            exitCheckout();

            return;
        }

        dispatch(changeState({ paymentCart: cartData }));
        dispatch(changeState({ isDataProcessing: false }));
    }, [cartData.loaded, cartData.cartItems?.length, isMaterialLoaded, isMaterialError, material]);
}
